/**
 * useSessionStore — Focus session history & queries.
 *
 * Storage: 'sessions' (collection)
 * Entity: { id, duration, identityId, taskId, projectId, createdAt }
 * Methods: addSession, getSessions, getSessionsByDate, getTotalFocusTime
 */
import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';

const STORAGE_KEY = 'sessions';

const useSessionStore = create((set, get) => ({
    sessions: storage.get(STORAGE_KEY, []),

    addSession(session) {
        const newSession = {
            ...session,
            id: crypto.randomUUID(),
            createdAt: new Date().toISOString(),
        };
        set(state => {
            const updated = [newSession, ...state.sessions];
            storage.set(STORAGE_KEY, updated);
            return { sessions: updated };
        });
        return newSession;
    },

    deleteSession(id) {
        set(state => {
            const updated = state.sessions.filter(s => s.id !== id);
            storage.set(STORAGE_KEY, updated);
            return { sessions: updated };
        });
    },

    getSessionsByIdentity(identityId) {
        return get().sessions.filter(s => s.identityId === identityId);
    },

    getSessionsByTag(tag) {
        return get().sessions.filter(s => s.tags?.includes(tag));
    },

    getSessionsByProject(project) {
        return get().sessions.filter(s => s.project === project);
    },

    getSessionsByTask(taskId) {
        return get().sessions.filter(s => s.taskId === taskId);
    },

    getSessionsByDateRange(startDate, endDate) {
        return get().sessions.filter(s => {
            const d = new Date(s.startTime);
            return d >= startDate && d <= endDate;
        });
    },

    getTodaySessions() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return get().sessions.filter(s => {
            const d = new Date(s.startTime);
            return d >= today && d < tomorrow;
        });
    },

    getTotalFocusTime() {
        return get().sessions.reduce((sum, s) => sum + (s.duration || 0), 0);
    },

    getTodayFocusTime() {
        return get().getTodaySessions().reduce((sum, s) => sum + (s.duration || 0), 0);
    },

    getAllTags() {
        const tags = new Set();
        get().sessions.forEach(s => s.tags?.forEach(t => tags.add(t)));
        return [...tags];
    },

    getAllProjects() {
        const projects = new Set();
        get().sessions.forEach(s => { if (s.project) projects.add(s.project); });
        return [...projects];
    },
}));

// Re-read from localStorage after cloud sync completes
onRehydrate(() => {
    useSessionStore.setState({ sessions: storage.get(STORAGE_KEY, []) });
});

export default useSessionStore;
